import type {
  ChatPlayerMessage,
  ChatPlayerPartTiming,
  ChatPlayerRole,
  ChatPlayerSyntheticToolConfig,
  ChatPlayerTimedWord,
  ChatPlayerTranscriptSegmentInput,
  ChatPlayerTranscriptSource,
  ChatPlayerTranscriptSpeaker,
} from "./chat-player-types";
import { normalizeChatPlayerCompiledChat } from "./chat-player-normalize";
import {
  clampMs,
  makeStableId,
  normalizeTimedWordSpacing,
  secondsToMs,
  synthesizeTimedWords,
} from "./chat-player-timing";

const SYNTHETIC_TOOL_OFFSET_MS = 150;
const SYNTHETIC_TOOL_DEFAULT_DURATION_MS = 1200;

type SyntheticToolAnchor = {
  endMs: number;
  insertIndex: number;
  message: ChatPlayerMessage;
};

export function compileChatPlayerTranscript(source: ChatPlayerTranscriptSource) {
  const messages: ChatPlayerMessage[] = [];
  const partTimings: ChatPlayerPartTiming[] = [];
  const speakerRoleMap = new Map<string, ChatPlayerRole>();

  source.segments.forEach((segment, index) => {
    const messageId = segment.id ?? `segment-${index}`;
    const partId = makeStableId([messageId, "speech"]) || `${messageId}-speech`;
    const startMs = resolveInputTimeMs(segment.startMs, segment.start_time, 0);
    const endMs = resolveInputTimeMs(segment.endMs, segment.end_time, startMs);
    const words = compileTranscriptWords(segment, partId, startMs, endMs);
    const revealStartMs = getTimedTextRevealStartMs(words, startMs);
    const role = resolveSpeakerRole(segment.speaker, index, source.speakerRoles, speakerRoleMap);

    messages.push({
      id: messageId,
      role,
      parts: [{ id: partId, type: "speech", words }],
    });
    partTimings.push({ messageId, partId, startMs: revealStartMs, endMs });
  });

  for (const syntheticTool of source.syntheticTools ?? []) {
    insertSyntheticTool(messages, partTimings, syntheticTool);
  }

  return normalizeChatPlayerCompiledChat({
    id: source.id ?? "transcript-chat",
    messages,
    partTimings,
  });
}

function compileTranscriptWords(
  segment: ChatPlayerTranscriptSegmentInput,
  partId: string,
  startMs: number,
  endMs: number,
) {
  if (!segment.words || segment.words.length === 0) {
    return normalizeTimedWordSpacing(
      synthesizeTimedWords({
        text: segment.text,
        idPrefix: partId,
        startMs,
        endMs,
      }),
    );
  }

  return normalizeTimedWordSpacing(
    segment.words.map((word, index) => {
      const wordStartMs = resolveInputTimeMs(word.startMs, word.start_time, startMs);
      const wordEndMs = resolveInputTimeMs(word.endMs, word.end_time, wordStartMs);

      return {
        id: word.id ?? (makeStableId([partId, "word", index]) || `${partId}-word-${index}`),
        text: word.text,
        startMs: wordStartMs,
        endMs: Math.max(wordStartMs, wordEndMs),
      };
    }) satisfies ChatPlayerTimedWord[],
  );
}

function getTimedTextRevealStartMs(words: ChatPlayerTimedWord[], fallbackStartMs: number) {
  return words.find(word => word.text.trim().length > 0)?.startMs ?? fallbackStartMs;
}

function insertSyntheticTool(
  messages: ChatPlayerMessage[],
  partTimings: ChatPlayerPartTiming[],
  syntheticTool: ChatPlayerSyntheticToolConfig,
) {
  const anchor = resolveSyntheticToolAnchor(messages, partTimings, syntheticTool);

  if (!anchor) {
    return;
  }

  const toolStartMs = anchor.endMs + SYNTHETIC_TOOL_OFFSET_MS;
  const toolEndMs = toolStartMs + (syntheticTool.durationMs ?? SYNTHETIC_TOOL_DEFAULT_DURATION_MS);
  const partId = makeStableId([anchor.message.id, syntheticTool.id]) || syntheticTool.id;

  anchor.message.parts.splice(anchor.insertIndex + 1, 0, {
    id: partId,
    type: "tool",
    name: syntheticTool.id,
    data: {
      loadingText: syntheticTool.loadingText,
      successText: syntheticTool.successText,
      errorText: syntheticTool.errorText,
      result: syntheticTool.result === "error" ? "error" : "success",
    },
  });

  partTimings.push({
    messageId: anchor.message.id,
    partId,
    startMs: toolStartMs,
    endMs: toolEndMs,
  });
}

function resolveSyntheticToolAnchor(
  messages: ChatPlayerMessage[],
  partTimings: ChatPlayerPartTiming[],
  syntheticTool: ChatPlayerSyntheticToolConfig,
): SyntheticToolAnchor | null {
  const message = messages.find(candidate => candidate.id === syntheticTool.anchor.messageId);

  if (!message) {
    return null;
  }

  const anchorPartId = syntheticTool.anchor.partId ?? message.parts.at(-1)?.id;

  if (!anchorPartId) {
    return null;
  }

  const anchorIndex = message.parts.findIndex(part => part.id === anchorPartId);

  if (anchorIndex === -1) {
    return null;
  }

  const anchorTiming = partTimings.find(timing => timing.messageId === message.id && timing.partId === anchorPartId);

  if (!anchorTiming) {
    return null;
  }

  return {
    endMs: anchorTiming.endMs ?? anchorTiming.startMs,
    insertIndex: anchorIndex,
    message,
  };
}

function resolveSpeakerRole(
  speaker: ChatPlayerTranscriptSpeaker | undefined,
  index: number,
  explicitRoles: Record<string, ChatPlayerRole> | undefined,
  inferredRoles: Map<string, ChatPlayerRole>,
) {
  const speakerId = getSpeakerId(speaker) ?? `speaker-${index}`;
  const explicitRole = explicitRoles?.[speakerId];

  if (explicitRole) {
    return explicitRole;
  }

  const inferredRole = inferredRoles.get(speakerId);

  if (inferredRole) {
    return inferredRole;
  }

  const role: ChatPlayerRole = inferredRoles.size % 2 === 0 ? "assistant" : "user";
  inferredRoles.set(speakerId, role);

  return role;
}

function getSpeakerId(speaker: ChatPlayerTranscriptSpeaker | undefined) {
  if (!speaker) {
    return undefined;
  }

  return typeof speaker === "string" ? speaker : speaker.id;
}

function resolveInputTimeMs(msValue: number | undefined, secondsValue: number | undefined, fallbackMs: number) {
  if (typeof msValue === "number") {
    return clampMs(msValue);
  }

  return secondsToMs(secondsValue) ?? clampMs(fallbackMs);
}
